import React, { useEffect, useMemo, useState } from 'react';
import { Card, Empty, Tabs, Typography } from 'antd';
import BillingDocumentTypesManagement from './BillingDocumentTypesManagement';
import FiscalConfiguration from './FiscalConfiguration';
import PaymentMethodsManagement from './PaymentMethodsManagement';
import OrganizationRefactoringReasonsManagement from './OrganizationRefactoringReasonsManagement';
import OrganizationTaxConfigurationManagement from './OrganizationTaxConfigurationManagement';
import ElectronicSriConfigurationManagement from './ElectronicSriConfigurationManagement';
import { ProfileResponse } from '../store/user/types';

interface BillingConfigurationProps {
    getProfile: any;
    getOrganizationManagementCenters?: (organizationId: string) => Promise<any>;
}

function getOrganization(profile?: ProfileResponse | null) {
    const contract = profile?.contract || profile?.Contract || {};
    return {
        id: String(contract.organizationId || contract.OrganizationId || profile?.organizationId || ''),
        name: String(contract.organizationName || contract.OrganizationName || profile?.organizationName || '')
    };
}

/**
 * Configuración de facturación de una organización.
 * Las políticas globales de plataforma no se editan desde esta pantalla.
 */
function BillingConfiguration({ getProfile, getOrganizationManagementCenters }: BillingConfigurationProps) {
    const [profile, setProfile] = useState<ProfileResponse | null>(null);

    useEffect(() => {
        let mounted = true;
        void getProfile?.().then((response: ProfileResponse | undefined) => {
            if (mounted) setProfile(response || null);
        });
        return () => {
            mounted = false;
        };
    }, [getProfile]);

    const organization = useMemo(() => getOrganization(profile), [profile]);

    return (
        <Card
            title="Configuración"
            extra={<Typography.Text type="secondary">Configuración de la organización</Typography.Text>}
        >
            <Tabs
                items={[
                    {
                        key: 'fiscal',
                        label: 'Configuración fiscal',
                        children: (
                            <FiscalConfiguration
                                getProfile={getProfile}
                                getOrganizationManagementCenters={getOrganizationManagementCenters}
                            />
                        )
                    },
                    {
                        key: 'billing-document-types',
                        label: 'Tipos de comprobante',
                        children: organization.id ? (
                            <BillingDocumentTypesManagement
                                organizationId={organization.id}
                                organizationName={organization.name}
                            />
                        ) : <Empty description="No se pudo identificar la organización." />
                    },
                    {
                        key: 'payment-methods',
                        label: 'Métodos de pago',
                        children: organization.id ? (
                            <PaymentMethodsManagement
                                organizationId={organization.id}
                                organizationName={organization.name}
                            />
                        ) : <Empty description="No se pudo identificar la organización." />
                    },
                    {
                        key: 'refactoring-reasons',
                        label: 'Motivos de refacturación',
                        children: organization.id ? <OrganizationRefactoringReasonsManagement organizationId={organization.id} /> : <Empty description="No se pudo identificar la organización." />
                    },
                    {
                        key: 'taxes',
                        label: 'Impuestos / IVA',
                        children: organization.id ? <OrganizationTaxConfigurationManagement organizationId={organization.id} organizationName={organization.name} /> : <Empty description="No se pudo identificar la organización." />
                    },
                    {
                        key: 'electronic-sri',
                        label: 'Facturación electrónica SRI',
                        children: organization.id ? <ElectronicSriConfigurationManagement organizationId={organization.id} /> : <Empty description="No se pudo identificar la organización." />
                    }
                ]}
            />
        </Card>
    );
}

export default BillingConfiguration;
